import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Hook para cerrar sesión automáticamente por inactividad
 * @param {number} timeoutMinutes - Minutos de inactividad antes de cerrar sesión
 * @param {string} logoutPath - Ruta a la que redirigir después del logout
 * @param {string} tokenKey - Key del localStorage para el token
 * @param {string[]} additionalKeys - Keys adicionales del localStorage a limpiar
 * @param {string} warningMessage - Mensaje de advertencia antes del cierre
 */
export function useInactivityLogout({
  timeoutMinutes,
  logoutPath,
  tokenKey,
  additionalKeys = [],
  warningMessage = 'Tu sesión ha expirado por inactividad'
}) {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  const clearSession = useCallback(() => {
    // Limpiar token principal
    localStorage.removeItem(tokenKey);
    
    // Limpiar keys adicionales
    additionalKeys.forEach(key => localStorage.removeItem(key));
    
    // Mostrar mensaje y redirigir
    toast.warning(warningMessage);
    navigate(logoutPath);
  }, [tokenKey, additionalKeys, logoutPath, navigate, warningMessage]);

  const resetTimer = useCallback(() => {
    // Limpiar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Solo configurar timer si hay un token activo
    const token = localStorage.getItem(tokenKey);
    if (!token) return;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = timeoutMs - 30000; // Advertencia 30 segundos antes

    // Timer de advertencia (30 segundos antes)
    if (warningMs > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        toast.info('Tu sesión expirará en 30 segundos por inactividad', {
          duration: 5000
        });
      }, warningMs);
    }

    // Timer de cierre de sesión
    timeoutRef.current = setTimeout(clearSession, timeoutMs);
  }, [timeoutMinutes, tokenKey, clearSession]);

  useEffect(() => {
    // Eventos que indican actividad del usuario
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Iniciar timer
    resetTimer();

    // Agregar listeners para resetear timer en actividad
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);

  return { resetTimer };
}
